import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);

  async function loadAll() {
    try {
      const [o, p] = await Promise.all([
        axios.get(`${API}/admin/shop/orders?status=pending`, { withCredentials: true }),
        axios.get(`${API}/shop/products`),
      ]);
      setOrders(o.data.orders || []);
      setProducts(p.data.products || []);
    } catch (e) { /* ignore */ }
  }
  useEffect(() => { if (user?.role === "owner") loadAll(); }, [user]);

  if (!user || user.role !== "owner") return null;

  async function ship(orderId) {
    const tn = prompt("Tracking number (optional):");
    try {
      await axios.post(`${API}/admin/shop/orders/${orderId}/ship`, { tracking_number: tn || "", origin_url: window.location.origin }, { withCredentials: true });
      toast.success(`Order ${orderId.slice(0, 12)}… marked shipped.`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ship failed.");
    }
  }

  async function deleteProduct(pid) {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/shop/products/${pid}`, { withCredentials: true });
      toast.success("Deleted.");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  }

  async function saveProduct(p) {
    const url = p.product_id ? `${API}/shop/products/${p.product_id}` : `${API}/shop/products`;
    const method = p.product_id ? "put" : "post";
    try {
      await axios[method](url, p, { withCredentials: true });
      toast.success(p.product_id ? "Updated." : "Created.");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  }

  return (
    <div
      data-testid="shop-admin"
      className="bg-[#0F0F0F] border border-red-900/60 p-6 mt-6"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-1">
        Owner Tools · Shop
      </div>
      <h2 className="font-display text-2xl text-white mb-4">
        The <span className="text-[#D4AF37]">Phoenix Shop</span> Admin
      </h2>
      <div className="flex gap-2 mb-5 border-b border-zinc-900 pb-3">
        {["orders", "products"].map((t) => (
          <button
            key={t}
            data-testid={`shop-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border transition-colors ${
              tab === t
                ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                : "border-zinc-800 text-neutral-500 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            }`}
          >
            {t === "orders" ? `Pending Orders (${orders.length})` : `Products (${products.length})`}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        orders.length === 0 ? (
          <p className="text-sm text-neutral-500 py-6 text-center">No pending orders. 🎉</p>
        ) : (
          <ul className="divide-y divide-zinc-900 border border-zinc-900">
            {orders.map((o) => (
              <li key={o.order_id} data-testid={`admin-order-${o.order_id}`} className="p-4">
                <div className="flex justify-between gap-3 items-start mb-3">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white">
                      {o.user_name} <span className="text-neutral-500">· {o.user_email}</span>
                    </div>
                    <div className="text-[10px] text-neutral-600 font-mono">{o.order_id}</div>
                  </div>
                  <div className="font-mono tabular-nums font-bold text-[#D4AF37]">
                    {o.total_od} OD
                  </div>
                </div>
                <div className="text-sm text-neutral-400 mb-2">
                  {o.items.map((i) => (
                    <div key={i.product_id}>
                      {i.image} {i.name} × {i.qty}
                    </div>
                  ))}
                </div>
                <div className="text-xs text-neutral-500 bg-[#0A0A0A] border border-zinc-900 p-2 mb-3 leading-relaxed">
                  <strong className="text-white">{o.shipping_address.name}</strong><br />
                  {o.shipping_address.line1}{o.shipping_address.line2 ? `, ${o.shipping_address.line2}` : ""}<br />
                  {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}<br />
                  {o.shipping_address.country}
                </div>
                <button
                  onClick={() => ship(o.order_id)}
                  data-testid={`ship-${o.order_id}`}
                  className="bg-green-700 hover:bg-green-600 text-white font-bold uppercase tracking-widest text-xs px-4 py-2 transition-colors"
                >
                  Mark Shipped
                </button>
              </li>
            ))}
          </ul>
        )
      )}

      {tab === "products" && (
        <ProductsEditor products={products} onSave={saveProduct} onDelete={deleteProduct} />
      )}
    </div>
  );
}

function emptyProduct() {
  return { name: "", description: "", price_od: 100, image: "🎁", stock: 10, active: true };
}

function ProductsEditor({ products, onSave, onDelete }) {
  const [draft, setDraft] = useState(emptyProduct());
  function field(k, v) { setDraft({ ...draft, [k]: v }); }

  return (
    <div>
      {/* Existing products */}
      <div className="space-y-2 mb-5">
        {products.map((p) => (
          <ProductRow key={p.product_id} product={p} onSave={onSave} onDelete={onDelete} />
        ))}
      </div>

      {/* Create new */}
      <div className="bg-[#0A0A0A] border border-[#D4AF37]/40 p-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
          Add new product
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          <input data-testid="new-product-name" className="col-span-2 bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" placeholder="Name" value={draft.name} onChange={(e) => field("name", e.target.value)} />
          <input className="bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" placeholder="Icon" value={draft.image} onChange={(e) => field("image", e.target.value)} />
          <input className="bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" placeholder="Stock" type="number" value={draft.stock} onChange={(e) => field("stock", parseInt(e.target.value, 10) || 0)} />
        </div>
        <textarea className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none focus:border-[#D4AF37] mb-2" rows={2} placeholder="Description" value={draft.description} onChange={(e) => field("description", e.target.value)} />
        <div className="flex gap-2 items-center">
          <input className="bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none focus:border-[#D4AF37] w-32" placeholder="Price OD" type="number" value={draft.price_od} onChange={(e) => field("price_od", parseInt(e.target.value, 10) || 0)} />
          <button
            onClick={() => { onSave(draft); setDraft(emptyProduct()); }}
            data-testid="new-product-save"
            disabled={!draft.name || draft.price_od <= 0}
            className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-widest text-xs px-4 py-2 transition-all disabled:opacity-40"
          >
            + Add Product
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductRow({ product, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(product);

  if (!editing) {
    return (
      <div className="bg-[#0A0A0A] border border-zinc-900 p-3 flex items-center gap-3">
        <span className="text-3xl">{product.image}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white truncate">{product.name}</div>
          <div className="text-[10px] text-neutral-500">{product.price_od} OD · {product.stock} in stock {product.active ? "" : "· INACTIVE"}</div>
        </div>
        <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-[#D4AF37] px-2 py-1">Edit</button>
        <button onClick={() => onDelete(product.product_id)} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-red-400 px-2 py-1">Delete</button>
      </div>
    );
  }
  return (
    <div className="bg-[#0A0A0A] border border-[#D4AF37] p-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
        <input className="col-span-2 bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <input className="bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none" value={draft.image} onChange={(e) => setDraft({ ...draft, image: e.target.value })} />
        <input type="number" className="bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none" value={draft.stock} onChange={(e) => setDraft({ ...draft, stock: parseInt(e.target.value, 10) || 0 })} />
      </div>
      <textarea rows={2} className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none mb-2" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
      <div className="flex flex-wrap gap-2 items-center">
        <input type="number" className="bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 text-sm outline-none w-32" value={draft.price_od} onChange={(e) => setDraft({ ...draft, price_od: parseInt(e.target.value, 10) || 0 })} />
        <label className="text-xs text-neutral-400 flex items-center gap-2">
          <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Active
        </label>
        <div className="flex-1" />
        <button onClick={() => setEditing(false)} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white px-2 py-1">Cancel</button>
        <button onClick={() => { onSave(draft); setEditing(false); }} className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-widest text-xs px-4 py-2 transition-all">Save</button>
      </div>
    </div>
  );
}
